import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Loader2, ClipboardList } from 'lucide-react';
import { type StatusItem, type StatusCategory, STATUS_OPTIONS, formatStatus } from './types';

interface StatusUpdateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: StatusItem | null;
  onSubmit: (statusItemId: string, newStatus: string, comment?: string) => Promise<void>;
}

export function StatusUpdateModal({ open, onOpenChange, item, onSubmit }: StatusUpdateModalProps) {
  const { t } = useTranslation('scout');
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (item && open) {
      setSelectedStatus(item.currentStatus);
      setComment('');
      setError(null);
    }
  }, [item, open]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        onOpenChange(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onOpenChange]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  if (!open || !item) return null;

  const category = item.statusType.category as StatusCategory;
  const options = STATUS_OPTIONS[category] || [];

  const handleSubmit = async () => {
    if (!selectedStatus) return;
    navigator.vibrate?.(50);
    setIsSubmitting(true);
    setError(null);
    try {
      await onSubmit(item.id, selectedStatus, comment || undefined);
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('status.updateError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) onOpenChange(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-white"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-gradient-to-r from-primary to-forest text-white">
        <div className="flex items-center gap-3">
          <span className="bg-white/20 w-10 h-10 flex items-center justify-center rounded-xl" aria-hidden="true">
            {item.statusType.icon ? (
              <span className="text-2xl">{item.statusType.icon}</span>
            ) : (
              <ClipboardList className="w-6 h-6 text-white" />
            )}
          </span>
          <div>
            <h2 id="modal-title" className="font-semibold text-white">
              {item.statusType.name}
            </h2>
            <p className="text-small text-white/70">
              {t('status.current')}: <span className="capitalize">{formatStatus(item.currentStatus)}</span>
            </p>
          </div>
        </div>
        <button
          onClick={handleClose}
          disabled={isSubmitting}
          className="p-2 rounded-full hover:bg-white/20 transition-colors"
          aria-label={t('status.close')}
        >
          <X className="h-5 w-5 text-white" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        <p className="text-small font-medium mb-4 text-foreground">{t('status.whatsTheStatus')}</p>

        <div className="space-y-3">
          {options.map((option) => {
            const isSelected = selectedStatus === option.value;
            const isCurrent = item.currentStatus === option.value;

            return (
              <button
                key={option.value}
                onClick={() => setSelectedStatus(option.value)}
                disabled={isSubmitting}
                className={`w-full p-4 rounded-xl border-2 text-start transition-all focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                  isSelected ? 'border-primary bg-primary/5' : 'border-warm-border hover:border-primary/30'
                }`}
                aria-pressed={isSelected}
              >
                <div className="flex items-center gap-3">
                  <span
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-lg ${
                      isSelected ? 'bg-primary/10' : 'bg-muted'
                    }`}
                    aria-hidden="true"
                  >
                    {option.icon}
                  </span>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-foreground">{t(`status.levels.${option.labelKey}`)}</span>
                      {isCurrent && (
                        <span className="text-caption bg-muted text-muted-foreground px-2 py-0.5 rounded">
                          {t('status.current')}
                        </span>
                      )}
                    </div>
                    <p className="text-small text-muted-foreground mt-0.5">
                      {t(`status.descriptions.${option.descriptionKey}`)}
                    </p>
                  </div>
                  {isSelected && (
                    <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                      <span className="text-white text-small">{'\u2713'}</span>
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Comment field */}
        <div className="mt-4 space-y-2">
          <label htmlFor="status-comment" className="block text-small font-medium text-foreground">
            {t('status.addComment')}
          </label>
          <textarea
            id="status-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t('status.commentPlaceholder')}
            rows={3}
            maxLength={500}
            disabled={isSubmitting}
            className="w-full px-4 py-3 rounded-xl border border-warm-border bg-white text-small focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
          />
          <p className="text-caption text-muted-foreground text-end">{comment.length}/500</p>
        </div>

        {/* Error message */}
        {error && (
          <div className="mt-4 p-3 bg-critical/5 border border-critical/10 text-critical rounded-lg text-small">
            {error}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-warm-border">
        <div className="flex gap-3">
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="flex-1 h-12 rounded-xl border border-warm-border text-foreground font-medium text-small hover:bg-warm-faint transition-colors"
          >
            {t('status.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={!selectedStatus || isSubmitting}
            className="flex-1 h-12 rounded-xl bg-gradient-to-r from-primary to-forest text-white font-medium text-small disabled:opacity-50 flex items-center justify-center gap-2 active:scale-[0.98] transition-all"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('status.updating')}
              </>
            ) : (
              t('status.submit')
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
